import React, { useState } from "react";
import { Settings } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import InfoTile from "../../../components/common/InfoTile";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetFuelVehiclesQuery } from "../fuelApi";
import { useCanManageFuelCards } from "../useFuelPermissions";
import { formatDate, litres, whole } from "../fuelFormat";
import VehicleFuelSetupModal from "../components/VehicleFuelSetupModal";
import VehicleTypeSelect from "../../masters/vehicles/components/VehicleTypeSelect";

// Vehicle <-> Fuel Card assignment, from the vehicle's side (Fuel Cards >
// Manage is the same relationship from the card's side). Many-to-many: a card
// can serve many vehicles and a vehicle can hold many cards, one of them its
// default. Also where a vehicle's fuel type and tank capacity are set - both
// feed the checks run on every purchase.
const VehicleFuelSetupPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const canManage = useCanManageFuelCards();
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [onlyWithoutCard, setOnlyWithoutCard] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);

  const { data: vehicles = [], isLoading } = useGetFuelVehiclesQuery({
    branchId: selectedBranchId ?? undefined,
    includeInactive: includeInactive || undefined,
    vehicleType: vehicleType || undefined,
  });

  const q = search.trim().toLowerCase();
  const rows = vehicles.filter(
    (v) =>
      (!onlyWithoutCard || !v.cardCount) &&
      (!q ||
        String(v.regNo || "").toLowerCase().includes(q) ||
        `${v.brand || ""} ${v.model || ""}`.toLowerCase().includes(q) ||
        String(v.allocatedToEmployeeName || "").toLowerCase().includes(q) ||
        String(v.defaultCardNumber || "").toLowerCase().includes(q))
  );

  // Looked up from the live list (not a stale copy) so the modal follows its own saves.
  const editingVehicle = vehicles.find((v) => v.vehicleId === editingVehicleId) || null;

  const withoutCard = vehicles.filter((v) => !v.cardCount).length;
  const withoutProfile = vehicles.filter((v) => !v.fuelType || v.tankCapacityLitres == null).length;

  const columns = [
    {
      title: "Vehicle",
      dataIndex: "regNo",
      render: (v, r) => (
        <>
          <span className="fw-semibold">{v}</span>
          {!r.isActive && <span className="badge badge-linedanger ms-2">Inactive</span>}
          <div className="small text-muted">{r.brand} {r.model} · {r.branchCode}</div>
        </>
      ),
    },
    { title: "Allocated to", dataIndex: "allocatedToEmployeeName", render: (v) => v || "-" },
    { title: "Fuel type", dataIndex: "fuelType", render: (v) => v || <span className="text-muted">not set</span> },
    {
      title: "Tank (L)",
      dataIndex: "tankCapacityLitres",
      align: "right",
      render: (v) => (v == null ? <span className="text-muted">not set</span> : litres(v)),
    },
    {
      title: "Default card",
      dataIndex: "defaultCardNumber",
      render: (v, r) =>
        v ? <span className="fw-semibold">{v}</span> : <span className="text-muted">{r.cardCount ? "no default chosen" : "no card"}</span>,
    },
    {
      title: "Cards",
      dataIndex: "cardCount",
      render: (v) => (v ? <span className="badge badge-lineinfo">{v} card{v === 1 ? "" : "s"}</span> : "-"),
    },
    {
      title: "Last odometer",
      dataIndex: "lastOdometer",
      render: (v, r) =>
        v == null ? (
          "-"
        ) : (
          <>
            {whole(v)} km
            <div className="small text-muted">{formatDate(r.lastOdometerDate)}</div>
          </>
        ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, r) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
          onClick={() => setEditingVehicleId(r.vehicleId)}
        >
          <Settings size={14} /> {canManage ? "Set up" : "Open"}
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Vehicle Fuel Setup</h4>
              <h6>Which fuel cards each vehicle uses, plus its fuel type and tank size - a vehicle can have several cards, and a card several vehicles</h6>
            </div>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3"><InfoTile label="Vehicles" value={vehicles.length} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Without a fuel card" tone={withoutCard ? "maroon" : "neutral"} value={withoutCard} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Fuel type / tank not set" value={withoutProfile} /></div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Reg no, make, allocated to, card"
                    className="form-control form-control-sm formsearch"
                    style={{ minWidth: 240, height: 34 }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-3 align-items-center flex-wrap">
                <VehicleTypeSelect className="form-control form-control-sm" style={{ width: 150, height: 34 }} value={vehicleType} onChange={setVehicleType} allLabel="All Types" />
                <div className="form-check form-switch mb-0">
                  <input type="checkbox" className="form-check-input" id="fuelVehiclesNoCard" checked={onlyWithoutCard} onChange={(e) => setOnlyWithoutCard(e.target.checked)} />
                  <label className="form-check-label" htmlFor="fuelVehiclesNoCard">Without a card only</label>
                </div>
                <div className="form-check form-switch mb-0">
                  <input type="checkbox" className="form-check-input" id="fuelVehiclesIncludeInactive" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
                  <label className="form-check-label" htmlFor="fuelVehiclesIncludeInactive">Include inactive</label>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={rows} rowKey="vehicleId" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>

      <VehicleFuelSetupModal vehicle={editingVehicle} canManage={canManage} onClose={() => setEditingVehicleId(null)} />
    </div>
  );
};

export default VehicleFuelSetupPage;
